import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import Icon from '../../../_shared/components/Icon';
import Alert from '../../../_shared/components/Alert';
import {
  FilterNames,
  ListVariant,
  TradeState,
  TradeStateName
} from '../../../_shared/types/trade';
import { ListVariantSource } from '../../../_shared/types/auction';
import TradeListService from '../../../services/TradeListService';
import MixpanelService from '../../../services/MixpanelService';
import MixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import ReducerConstants from '../../../_shared/constants/reducers';
import copyToClipBoard from '../../../_shared/utils/copyToClipBoard';
import useOutsideClickListener from '../../../_shared/hooks/useOutsideClickListener';
import useIsBuyContext from '../../../_shared/hooks/trade/useIsBuyContext';
import useFeatureFlags from '../../../_shared/hooks/useFeatureFlags';
import FEATURE_FLAGS from '../../../_shared/constants/featureFlags';
import {
  ControlsButton,
  ControlsButtonWrapper,
  StyledFooterControls,
  StyledTradeOption
} from './Styles';
import TRToast from '../../../_shared/helpers/TRToast';

const Controls = ({
  callToActionText,
  dropdownMenuId,
  auctionSummary,
  setShouldShowTrade,
  tradeId,
  variant,
  vin,
  watchList
}) => {
  const tradeListService = TradeListService.get();
  const mixpanel = MixpanelService.get();
  const intl = useIntl();
  const isRemoveOpenToDgnEnabled = useFeatureFlags(
    FEATURE_FLAGS.IS_REMOVE_OPEN_TO_DGN_ENABLED
  );
  const kebabMenuRef = useRef(null);
  const controlsButtonRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const isBuyContext = useIsBuyContext();
  const [tradeState, user, activeFilter] = useSelector(
    state => [
      state?.ui?.nav?.secondary?.context,
      state[ReducerConstants.AUTH]?.user,
      state?.filter?.activeFilter
    ],
    shallowEqual
  );

  // Logic to determine if impound option should be displayed
  const canImpound = useCallback(() => {
    const isActiveOrUpcoming =
      tradeState === TradeState.active || tradeState === TradeState.upcoming;
    const isNotTopBidder = auctionSummary?.topBidderSummary?.id !== user?.id;

    return isBuyContext && isNotTopBidder && isActiveOrUpcoming && !watchList;
  }, [
    auctionSummary?.topBidderSummary?.id,
    isBuyContext,
    tradeState,
    user?.id,
    watchList
  ]);

  const onImpoundClick = useCallback(
    async id => {
      let impoundClicked;
      let isUnimpound = false;
      if (activeFilter?.id === FilterNames.impound.id) {
        isUnimpound = true;
        impoundClicked = await tradeListService.unimpoundTrade(id);
        mixpanel.track(MixpanelConfig.names.removeImpound, {
          source: ListVariantSource[variant],
          trade_id: tradeId,
          auction_id: auctionSummary?.id,
          trade_state: TradeStateName[tradeState]
        });
      } else {
        impoundClicked = await tradeListService.impoundTrade(id);
        mixpanel.track(MixpanelConfig.names.impoundTrade, {
          source: ListVariantSource[variant],
          trade_id: tradeId,
          auction_id: auctionSummary?.id,
          trade_state: TradeStateName[tradeState]
        });
      }

      // Display appropriate modal
      if (impoundClicked?.ok) {
        setShouldShowTrade(false);
        if (isUnimpound) {
          TRToast(
            <Alert
              variant="success"
              messages={[
                <FormattedMessage id="list.options.unimpound.success" />
              ]}
            />
          );
        } else {
          TRToast(
            <Alert
              variant="success"
              messages={[
                <FormattedMessage id="list.options.impound.success" />
              ]}
            />
          );
        }
      } else {
        // eslint-disable-next-line no-lonely-if
        if (isUnimpound) {
          TRToast(
            <Alert
              variant="error"
              messages={[
                <FormattedMessage id="list.options.unimpound.error" />
              ]}
            />
          );
        } else {
          TRToast(
            <Alert
              variant="error"
              messages={[<FormattedMessage id="list.options.impound.error" />]}
            />
          );
        }
      }
    },
    [
      activeFilter?.id,
      setShouldShowTrade,
      mixpanel,
      auctionSummary?.id,
      tradeId,
      tradeState,
      tradeListService,
      variant
    ]
  );

  const options = useMemo(
    () => [
      {
        label:
          activeFilter?.id === FilterNames.impound.id
            ? intl.formatMessage({ id: 'list.options.unimpound' })
            : intl.formatMessage({ id: 'list.options.impound' }),

        id:
          activeFilter?.id === FilterNames.impound.id ? 'unimpound' : 'impound',
        show: canImpound(),
        onClick: () => onImpoundClick(tradeId)
      },
      {
        label: `${intl.formatMessage({ id: 'vdp.overview.copyVin' })} ${vin}`,
        id: 'copyToClipboard',
        show: true,
        onClick: () => {
          copyToClipBoard(vin);
          TRToast(
            <Alert
              variant="information"
              messages={[<FormattedMessage id="vdp.success.vinCopied" />]}
            />
          );

          setIsOpen(false);

          mixpanel.track(MixpanelConfig.names.copyVinToClipBoard, {
            source: ListVariantSource[variant],
            trade_id: tradeId,
            auction_id: auctionSummary?.id,
            trade_state: TradeStateName[tradeState]
          });
        }
      },
      {
        label: intl.formatMessage({ id: 'list.options.removeVehicle' }),
        id: 'remove',
        show: false,
        onClick: () => {
          return null;
        }
      },
      {
        label: intl.formatMessage({ id: 'list.options.deleteVehicle' }),
        id: 'delete',
        show: false,
        onClick: () => {
          return null;
        }
      },
      {
        label: intl.formatMessage({ id: 'list.options.launchToActive' }),
        id: 'launch_to_active',
        show: false,
        onClick: () => {
          return null;
        }
      },
      {
        label: intl.formatMessage({ id: 'tr5.controls.parkVehicle' }),
        id: 'park',
        show: [TradeState.pending].includes(tradeState),
        onClick: () => {
          return null;
        }
      },
      {
        label: intl.formatMessage({ id: 'tr5.controls.editVehicle' }),
        id: 'edit',
        show: [TradeState.pending].includes(tradeState),
        onClick: () => {
          return null;
        }
      },
      {
        label: intl.formatMessage({ id: 'tr5.controls.auctionInDGN' }),
        id: 'auction',
        show:
          [TradeState.pending].includes(tradeState) &&
          !isRemoveOpenToDgnEnabled,
        onClick: () => {
          return null;
        }
      },
      {
        label: intl.formatMessage({ id: 'tr5.controls.sendToLost' }),
        id: 'lost',
        show: [TradeState.pending].includes(tradeState),
        onClick: () => {
          return null;
        }
      }
    ],
    [
      activeFilter?.id,
      auctionSummary?.id,
      canImpound,
      intl,
      isRemoveOpenToDgnEnabled,
      mixpanel,
      onImpoundClick,
      tradeId,
      tradeState,
      vin,
      variant
    ]
  );

  const [tradeOptions, setTradeOptions] = useState([]);

  useEffect(() => {
    const optionsToShow = options?.filter(option => option.show);

    setTradeOptions(optionsToShow || []);
  }, [options, tradeOptions?.length]);

  const onClickOutsideKebabMenu = () => {
    setIsOpen(false);
  };

  // use outside click listener only when kebab menu open
  useOutsideClickListener(
    kebabMenuRef,
    onClickOutsideKebabMenu,
    isOpen,
    controlsButtonRef
  );

  const openPanel = e => {
    e.stopPropagation();
    setIsOpen(open => !open);
  };

  const onSelectOption = onClick => event => {
    event.stopPropagation();
    if (onClick) {
      onClick(event);
    }
  };

  if (tradeOptions?.length === 0 || variant === ListVariant.SEARCH) {
    return null;
  }

  return (
    <>
      <ControlsButtonWrapper text={callToActionText}>
        {callToActionText}
        <ControlsButton
          data-testid={`tradeCard-${tradeId}-menu`}
          isOpen={isOpen}
          onClick={openPanel}
          variant={variant}
          ref={controlsButtonRef}
        >
          <Icon icon="TR5-Kebab" />
        </ControlsButton>
      </ControlsButtonWrapper>
      {isOpen && (
        <StyledFooterControls ref={kebabMenuRef} id={dropdownMenuId}>
          {tradeOptions.map(({ id, onClick, label }) => {
            return (
              <StyledTradeOption
                key={id}
                onClick={onSelectOption(onClick)}
                data-testid={`tradeOption-${tradeId}-${id}`}
                isCopy={id === 'copyToClipboard'}
              >
                {label}
              </StyledTradeOption>
            );
          })}
        </StyledFooterControls>
      )}
    </>
  );
};

export default Controls;
